import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { logout } from "../actions/user";
import instance from "../middleware/api";
import { currentAccessToken } from "@utils/viewAs";

export interface ICartItem {
  trackId: string;
  trackName: string;
  composerName: string;
  coverUrl: string;
  licenseType: "private" | "youtube-video" | "custom";
  licenseLabel: string;
  price: number;
  quantity: number;
}

export interface IBillingDetails {
  email: string;
  firstName: string;
  lastName: string;
  address: string;
  zipCode: string;
  country: string;
}

export type CheckoutStep = "cart" | "checkout" | "billing" | "tip" | "review" | "payment" | "plan-config" | "sub-review" | "sub-payment" | "upgrade-review" | "upgrade-payment";
export type TipType = "percent" | "custom" | "skip" | null;
export type CartMode = "license" | "subscription" | "subscription-upgrade";

export interface SubscriptionConfig {
  channels: number;
  claims: number;
  billingPeriod: "monthly" | "annual";
}

export interface SubscriptionUpgradeConfig {
  oldChannels: number;
  oldClaims: number;
  newChannels: number;
  newClaims: number;
  billingPeriod: "monthly" | "annual";
  oldTotal: number;
  newTotal: number;
  proratedAmount: number;
  daysLeftInPeriod: number;
  /** Total days in the current billing cycle (period_end − period_start).
      Used as the denominator for prorated credit math on monthly upgrades
      so we don't over/under-credit when the cycle straddles a 28/29/30/31-
      day calendar month. Optional for backward compat; defaults to 30 if
      omitted. */
  cycleLengthDays?: number;
  periodEndDate?: string;
  isDowngrade?: boolean;
}

export interface CartState {
  items: ICartItem[];
  isOpen: boolean;
  mode: CartMode;
  checkoutStep: CheckoutStep;
  tipType: TipType;
  tipAmount: number;
  billingDetails: IBillingDetails;
  waiverAccepted: boolean;
  termsAccepted: boolean;
  isLoggedInCheckout: boolean;
  paymentClientSecret: string | null;
  paymentIntentId: string | null;
  orderId: string | null;
  subscription: SubscriptionConfig;
  subscriptionUpgrade: SubscriptionUpgradeConfig | null;
  /** When true, SubPaymentView calls /stripe/subscriptions/switch-to-annual
      instead of /create. Set by openSwitchToAnnualCheckout, cleared by
      resetCheckout. The user already has an active monthly sub; this flow
      pays for the annual upfront and the webhook cancels the monthly sub. */
  isSwitchToAnnualMode: boolean;
  /** Credit-pause reactivation (ToU §17.4, Path B): the checkout was opened from
      the Account Issues modal / reminder email to reactivate a lapsed sub. Flags
      SubPaymentView to pass `reactivation:true` to /create and label the final
      button "Reactivate". */
  isReactivationMode: boolean;
  /** True from the moment a payment is submitted until it succeeds or fails.
      Blocks closing the cart so a charge can't be abandoned mid-flight. */
  paymentInFlight: boolean;
  /** The EU 14-day withdrawal waiver — §18 FAGG (one-off content) and §16 FAGG
   *  (continuing service). Used by EVERY paid checkout: subscription signup,
   *  switch-to-annual, and the single YouTube licence.
   *
   *  Deliberately separate from `waiverAccepted`, which in the single-licence
   *  flow means the unrelated CONTENT waiver ("my video is not focused on music
   *  or ambient content"). Those are two different consents recorded as two
   *  different documents (`digital_service_immediate_access_waiver` vs
   *  `licensing_waiver`); sharing one boolean is what made the older records
   *  ambiguous about what the user had actually agreed to.
   *
   *  In the store rather than component state because the PAYMENT view is what
   *  posts it, and it used to send a hardcoded `true`. */
  withdrawalWaiverAccepted: boolean;
}

const initialBilling: IBillingDetails = {
  email: "",
  firstName: "",
  lastName: "",
  address: "",
  zipCode: "",
  country: "",
};

// Load cart from localStorage
const loadCart = (): ICartItem[] => {
  try {
    const saved = localStorage.getItem("as-cart");
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

// Debounced server-side sync for logged-in users. localStorage stays the
// device cache + the guest cart; for an account the server (user_carts) is the
// source of truth so the abandoned-cart reminder cron can see it (the server
// can't read localStorage). Every sync counts as cart activity and resets the
// reminder cycle. See docu/flows/notifications/abandoned-cart.md.
let cartSyncTimer: ReturnType<typeof setTimeout> | null = null;
const syncCartToServer = (items: ICartItem[]) => {
  // Logged-in only. The Bearer token (localStorage "accessToken", set with an
  // expiry wrapper) is the auth source of truth; "as-cached-user" is the UI
  // cache. They can drift (e.g. an expired access token that still refreshes),
  // so gate on EITHER — both are cleared on logout. A 401 on the PUT is handled
  // by the refresh-retry interceptor.
  if (!currentAccessToken() && !localStorage.getItem("as-cached-user")) return;
  // Snapshot to a plain array NOW. `items` is usually an Immer draft (saveCart
  // runs inside the cart reducer); Immer revokes the draft once the reducer
  // returns, so the debounced PUT below (fires 800ms later) would throw on the
  // revoked proxy and the sync would die silently — the "added items never reach
  // the server" bug. The plain copy detaches it.
  const snapshot = items.map((it) => ({ ...it }));
  if (cartSyncTimer) clearTimeout(cartSyncTimer);
  cartSyncTimer = setTimeout(() => {
    instance.put("/cart", { items: snapshot }).catch(() => {});
  }, 800);
};

const saveCart = (items: ICartItem[]) => {
  localStorage.setItem("as-cart", JSON.stringify(items));
  syncCartToServer(items);
};

/**
 * Refresh cover art AND re-price every cart item against the catalog.
 *
 * The cart is restored from localStorage verbatim, price included, so a stored
 * price outlives any catalog change. That is not cosmetic: the server re-prices
 * at `POST /stripe/checkout/prepare` and REJECTS a mismatch as tampering, so a
 * cart held across a price change dies at Pay Now with "Cart pricing is out of
 * date — please refresh and try again" — advice that cannot work, because
 * reloading restores the same stale price from localStorage. The only escape
 * was to remove and re-add the item.
 *
 * Seen for real on dev (2026-07-19): a cart from before the 2026-07-16 licence
 * tiering held a track at $29 that had since become `licenseTier=plus` at $59.
 *
 * The price is recomputed with the SAME rule the server applies
 * (`repriceCart` → `singleLicensePriceForTier`): free track → WAV-upgrade
 * price, premium track → the single-licence price for its tier. A WAV-upgrade
 * line is identified by its stored price matching the WAV price, since the item
 * itself does not record which kind of purchase it is.
 */
export const refreshCartCovers = createAsyncThunk("cart/refreshCovers", async (_, { getState }) => {
  const { cart } = getState() as { cart: CartState };
  const uniqueIds = [...new Set(cart.items.map((item) => item.trackId))];
  const updates: Record<string, { coverUrl: string; tier?: string; licenseTier?: string }> = {};

  // Catalog prices, straight from the same endpoint the rest of the app uses.
  let config: { singleLicensePrice: number; plusLicensePrice: number; wavLicensePrice: number } | null = null;
  try {
    const cfg = await instance.get("/subscription-config");
    config = {
      singleLicensePrice: Number(cfg.data.singleLicensePrice),
      plusLicensePrice: Number(cfg.data.plusLicensePrice),
      wavLicensePrice: Number(cfg.data.wavLicensePrice),
    };
  } catch {
    // No config → leave prices untouched rather than guess. Checkout will still
    // reject, but we must not invent a number the catalog didn't give us.
  }

  await Promise.all(
    uniqueIds.map(async (id) => {
      try {
        const resp = await instance.get(`/tracks/${id}`);
        const track = resp.data;
        updates[id] = {
          coverUrl: track.mediumCoverUrl || track.largeCoverUrl || track.smallCoverUrl || "",
          tier: track.tier,
          licenseTier: track.licenseTier,
        };
      } catch {
        // skip failed fetches
      }
    }),
  );
  return { updates, config };
});

/**
 * On login, reconcile the account's server cart with the local (guest) cart:
 * union by track+license, keeping the larger quantity. The merged cart is set
 * + synced back. Logged-in only — no-ops for guests. See
 * docu/flows/notifications/abandoned-cart.md.
 */
export const mergeServerCart = createAsyncThunk("cart/mergeServer", async (_, { getState }) => {
  if (!currentAccessToken() && !localStorage.getItem("as-cached-user")) return null; // logged-in only (see syncCartToServer)
  let serverItems: ICartItem[] = [];
  try {
    const res = await instance.get("/cart");
    serverItems = Array.isArray(res.data?.items) ? res.data.items : [];
  } catch {
    return null; // never block on a cart fetch
  }
  const { cart } = getState() as { cart: CartState };
  const key = (it: ICartItem) => `${it.trackId}::${it.licenseType}`;
  const map = new Map<string, ICartItem>();
  for (const it of serverItems) map.set(key(it), it);
  for (const it of cart.items) {
    const k = key(it);
    const existing = map.get(k);
    if (existing) map.set(k, { ...existing, quantity: Math.max(existing.quantity, it.quantity) });
    else map.set(k, it);
  }
  return [...map.values()];
});

const initialSubscription: SubscriptionConfig = {
  channels: 1,
  claims: 10,
  billingPeriod: "monthly",
};

const initialState: CartState = {
  items: loadCart(),
  isOpen: false,
  mode: "license",
  checkoutStep: "cart",
  tipType: null,
  tipAmount: 0,
  billingDetails: initialBilling,
  waiverAccepted: false,
  termsAccepted: false,
  isLoggedInCheckout: false,
  paymentClientSecret: null,
  paymentIntentId: null,
  orderId: null,
  subscription: initialSubscription,
  subscriptionUpgrade: null,
  isSwitchToAnnualMode: false,
  isReactivationMode: false,
  paymentInFlight: false,
  withdrawalWaiverAccepted: false,
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart(state, action: PayloadAction<Omit<ICartItem, "quantity"> & { quantity?: number }>) {
      const { quantity: qty, ...itemData } = action.payload;
      const existingIdx = state.items.findIndex(
        (item) => item.trackId === itemData.trackId && item.licenseType === itemData.licenseType,
      );
      if (existingIdx >= 0) {
        // Same track + license already in the cart: ADD to the existing
        // quantity (clicking Add to Cart again always adds one more) and
        // refresh price/label in case they changed — don't reset to 1.
        state.items[existingIdx] = {
          ...itemData,
          quantity: state.items[existingIdx].quantity + (qty || 1),
        };
      } else {
        state.items.push({ ...itemData, quantity: qty || 1 });
      }
      saveCart(state.items);
    },
    removeFromCart(state, action: PayloadAction<number>) {
      state.items.splice(action.payload, 1);
      saveCart(state.items);
      if (state.items.length === 0 && state.mode === "license") {
        state.checkoutStep = "cart";
      }
    },
    updateQuantity(state, action: PayloadAction<{ index: number; change: number }>) {
      const item = state.items[action.payload.index];
      if (item) {
        // Minimum quantity is always 1 — the minus switcher never empties an
        // item (use the trash button to remove).
        item.quantity = Math.max(1, item.quantity + action.payload.change);
      }
      saveCart(state.items);
      if (state.items.length === 0 && state.mode === "license") {
        state.checkoutStep = "cart";
      }
    },
    clearCart(state) {
      state.items = [];
      saveCart(state.items);
      if (state.mode === "license") {
        state.checkoutStep = "cart";
      }
    },
    toggleCart(state) {
      state.isOpen = !state.isOpen;
      if (state.isOpen) {
        state.mode = "license";
        state.checkoutStep = "cart";
      }
    },
    openCart(state) {
      state.isOpen = true;
      state.mode = "license";
      state.checkoutStep = "cart";
    },
    openSubscriptionCheckout(state, action: PayloadAction<Partial<SubscriptionConfig>>) {
      state.isOpen = true;
      state.mode = "subscription";
      state.checkoutStep = "checkout";
      state.subscription = { ...initialSubscription, ...action.payload };
    },
    openSubscriptionUpgrade(state, action: PayloadAction<SubscriptionUpgradeConfig>) {
      state.isOpen = true;
      state.mode = "subscription-upgrade";
      state.checkoutStep = "upgrade-review";
      state.subscriptionUpgrade = action.payload;
      state.termsAccepted = false;
    },
    /** Open the cart in subscription-checkout mode pre-configured to switch
        an existing monthly sub to annual. Pre-fills channels/claims at the
        user's current values, jumps straight to plan-config, and flags
        SubPaymentView to use /switch-to-annual instead of /create. */
    openSwitchToAnnualCheckout(state, action: PayloadAction<{ channels: number; claims: number }>) {
      state.isOpen = true;
      state.mode = "subscription";
      state.checkoutStep = "plan-config";
      state.subscription = {
        channels: action.payload.channels,
        claims: action.payload.claims,
        billingPeriod: "annual",
      };
      state.isSwitchToAnnualMode = true;
      state.isReactivationMode = false;
      state.termsAccepted = false;
      state.withdrawalWaiverAccepted = false;
    },
    /** Credit-pause reactivation (ToU §17.4, Path B): open the standard
        subscription checkout pre-filled with the user's current configuration,
        so they reactivate a lapsed sub through the normal flow (full card +
        billing control). Flags SubPaymentView to pass reactivation:true and
        label the final button "Reactivate". */
    openReactivationCheckout(state, action: PayloadAction<{ channels: number; claims: number; billingPeriod: "monthly" | "annual" }>) {
      state.isOpen = true;
      state.mode = "subscription";
      state.checkoutStep = "plan-config";
      state.subscription = {
        channels: action.payload.channels,
        claims: action.payload.claims,
        billingPeriod: action.payload.billingPeriod,
      };
      state.isReactivationMode = true;
      state.isSwitchToAnnualMode = false;
      state.termsAccepted = false;
      state.withdrawalWaiverAccepted = false;
    },
    setSubscription(state, action: PayloadAction<Partial<SubscriptionConfig>>) {
      state.subscription = { ...state.subscription, ...action.payload };
    },
    closeCart(state) {
      state.isOpen = false;
    },
    /** A payment is in flight (confirmPayment → webhook order poll). While true
     *  the cart refuses to close: the ✕, Escape and Back must not abandon a
     *  charge mid-flight, which leaves the user staring at a page that says
     *  nothing while their card is being charged. Set by whichever payment view
     *  is submitting; cleared on success, failure, and cart reset. */
    setPaymentInFlight(state, action: PayloadAction<boolean>) {
      state.paymentInFlight = action.payload;
    },
    setCheckoutStep(state, action: PayloadAction<CheckoutStep>) {
      state.checkoutStep = action.payload;
    },
    setTip(state, action: PayloadAction<{ type: TipType; amount: number }>) {
      state.tipType = action.payload.type;
      state.tipAmount = action.payload.amount;
    },
    setBillingDetails(state, action: PayloadAction<Partial<IBillingDetails>>) {
      state.billingDetails = { ...state.billingDetails, ...action.payload };
    },
    setWaiverAccepted(state, action: PayloadAction<boolean>) {
      state.waiverAccepted = action.payload;
    },
    setTermsAccepted(state, action: PayloadAction<boolean>) {
      state.termsAccepted = action.payload;
    },
    setWithdrawalWaiverAccepted(state, action: PayloadAction<boolean>) {
      state.withdrawalWaiverAccepted = action.payload;
    },
    setIsLoggedInCheckout(state, action: PayloadAction<boolean>) {
      state.isLoggedInCheckout = action.payload;
    },
    setPaymentData(state, action: PayloadAction<{ clientSecret: string; paymentIntentId: string; orderId: string | null }>) {
      state.paymentClientSecret = action.payload.clientSecret;
      state.paymentIntentId = action.payload.paymentIntentId;
      state.orderId = action.payload.orderId;
    },
    resetCheckout(state) {
      state.mode = "license";
      state.checkoutStep = "cart";
      state.tipType = null;
      state.tipAmount = 0;
      state.billingDetails = initialBilling;
      state.waiverAccepted = false;
      state.termsAccepted = false;
      state.withdrawalWaiverAccepted = false;
      state.paymentClientSecret = null;
      state.paymentIntentId = null;
      state.orderId = null;
      state.subscription = initialSubscription;
      state.subscriptionUpgrade = null;
      state.isSwitchToAnnualMode = false;
      state.isReactivationMode = false;
      state.paymentInFlight = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(refreshCartCovers.fulfilled, (state, { payload }) => {
      const { updates, config } = payload;
      state.items.forEach((item) => {
        const fresh = updates[item.trackId];
        if (!fresh) return;
        if (fresh.coverUrl) item.coverUrl = fresh.coverUrl;
        if (!config) return;

        // Same rule as the server's repriceCart. A WAV-upgrade line is
        // recognised by its price rather than a flag, because the cart item
        // doesn't record which kind of purchase it is — so only re-price a
        // line we can confidently classify, and leave anything ambiguous
        // alone rather than silently changing what the user is buying.
        const isWavUpgrade = Math.abs(item.price - config.wavLicensePrice) < 0.005;
        if (isWavUpgrade) return;

        const authoritative = fresh.tier === "free"
          ? config.wavLicensePrice
          : (fresh.licenseTier === "plus" ? config.plusLicensePrice : config.singleLicensePrice);

        if (Number.isFinite(authoritative) && Math.abs(authoritative - item.price) >= 0.005) {
          item.price = authoritative;
        }
      });
      saveCart(state.items);
    });
    builder.addCase(mergeServerCart.fulfilled, (state, { payload }) => {
      // null = guest (no merge). Otherwise adopt the merged cart + sync back.
      if (payload) {
        state.items = payload;
        saveCart(state.items);
      }
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.isOpen = false;
      state.mode = "license";
      state.checkoutStep = "cart";
      state.tipType = null;
      state.tipAmount = 0;
      state.billingDetails = initialBilling;
      state.waiverAccepted = false;
      state.termsAccepted = false;
      state.withdrawalWaiverAccepted = false;
      state.isLoggedInCheckout = false;
      state.paymentClientSecret = null;
      state.paymentIntentId = null;
      state.orderId = null;
      state.subscription = initialSubscription;
      state.subscriptionUpgrade = null;
      state.isSwitchToAnnualMode = false;
      state.isReactivationMode = false;
    });
  },
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart,
  toggleCart,
  openCart,
  openReactivationCheckout,
  closeCart,
  setPaymentInFlight,
  setCheckoutStep,
  setTip,
  setBillingDetails,
  setWaiverAccepted,
  setTermsAccepted,
  setWithdrawalWaiverAccepted,
  setIsLoggedInCheckout,
  setPaymentData,
  resetCheckout,
  openSubscriptionCheckout,
  openSubscriptionUpgrade,
  openSwitchToAnnualCheckout,
  setSubscription,
} = cartSlice.actions;

export default cartSlice.reducer;
